import type { WalletKey } from "types/shared";

export enum COIN_CONSTANTS {
  ETH = "eth",
  BTC = "btc",
  TRX = "trx",
  USDT = "usdt",
  USDC = "usdc",
  LTC = "ltc",
  BNB = "bnb",
  PYUSD = "pyusd",
  SOL = "sol",
  TON = "ton",
}

export enum CHAIN_CONSTANTS {
  ERC20 = "erc20",
  BITCOIN = "bitcoin",
  BEP20 = "bep20",
  TRC20 = "trc20",
  ARBITRUM = "arbitrum",
  BASE = "base",
  SOLANA = "solana",
  TON = "ton",
  LITECOIN = "litecoin",
}

export type ChainNetworkInfo = {
  key: CHAIN_CONSTANTS;
  name: string;
  chainId: number;
  addressKey: WalletKey;
};

export const CHAIN_NETWORKS: Record<CHAIN_CONSTANTS, ChainNetworkInfo> = {
  [CHAIN_CONSTANTS.ERC20]: {
    key: CHAIN_CONSTANTS.ERC20,
    name: "Ethereum",
    chainId: 1,
    addressKey: COIN_CONSTANTS.ETH,
  },
  [CHAIN_CONSTANTS.BITCOIN]: {
    key: CHAIN_CONSTANTS.BITCOIN,
    name: "Bitcoin",
    chainId: 0,
    addressKey: COIN_CONSTANTS.BTC,
  },
  [CHAIN_CONSTANTS.BEP20]: {
    key: CHAIN_CONSTANTS.BEP20,
    name: "BNB Smart Chain",
    chainId: 56,
    addressKey: COIN_CONSTANTS.BNB,
  },
  [CHAIN_CONSTANTS.TRC20]: {
    key: CHAIN_CONSTANTS.TRC20,
    name: "TRON",
    chainId: 0,
    addressKey: COIN_CONSTANTS.TRX,
  },
  [CHAIN_CONSTANTS.ARBITRUM]: {
    key: CHAIN_CONSTANTS.ARBITRUM,
    name: "Arbitrum One",
    chainId: 42161,
    addressKey: COIN_CONSTANTS.ETH,
  },
  [CHAIN_CONSTANTS.BASE]: {
    key: CHAIN_CONSTANTS.BASE,
    name: "Base",
    chainId: 8453,
    addressKey: COIN_CONSTANTS.ETH,
  },
  [CHAIN_CONSTANTS.SOLANA]: {
    key: CHAIN_CONSTANTS.SOLANA,
    name: "Solana",
    chainId: 0,
    addressKey: COIN_CONSTANTS.SOL,
  },
  [CHAIN_CONSTANTS.TON]: {
    key: CHAIN_CONSTANTS.TON,
    name: "TON",
    chainId: 0,
    addressKey: COIN_CONSTANTS.TON,
  },
  [CHAIN_CONSTANTS.LITECOIN]: {
    key: CHAIN_CONSTANTS.LITECOIN,
    name: "Litecoin",
    chainId: 0,
    addressKey: COIN_CONSTANTS.LTC,
  },
};

/**
 * return the config for the coin
 * @example
 * COIN_CONFIG.eth.networks.erc20 → { key, name, chainId }
 */
export const COIN_CONFIG = {
  eth: {
    networks: {
      erc20: CHAIN_NETWORKS[CHAIN_CONSTANTS.ERC20],
      arbitrum: CHAIN_NETWORKS[CHAIN_CONSTANTS.ARBITRUM],
      base: CHAIN_NETWORKS[CHAIN_CONSTANTS.BASE],
      bep20: CHAIN_NETWORKS[CHAIN_CONSTANTS.BEP20],
    },
  },
  btc: { networks: { bitcoin: CHAIN_NETWORKS[CHAIN_CONSTANTS.BITCOIN] } },
  trx: { networks: { trc20: CHAIN_NETWORKS[CHAIN_CONSTANTS.TRC20] } },
  usdt: {
    networks: {
      trc20: CHAIN_NETWORKS[CHAIN_CONSTANTS.TRC20],
      erc20: CHAIN_NETWORKS[CHAIN_CONSTANTS.ERC20],
      bep20: CHAIN_NETWORKS[CHAIN_CONSTANTS.BEP20],
      solana: CHAIN_NETWORKS[CHAIN_CONSTANTS.SOLANA],
      ton: CHAIN_NETWORKS[CHAIN_CONSTANTS.TON],
    },
  },
  usdc: {
    networks: {
      erc20: CHAIN_NETWORKS[CHAIN_CONSTANTS.ERC20],
      bep20: CHAIN_NETWORKS[CHAIN_CONSTANTS.BEP20],
      arbitrum: CHAIN_NETWORKS[CHAIN_CONSTANTS.ARBITRUM],
      base: CHAIN_NETWORKS[CHAIN_CONSTANTS.BASE],
    },
  },
  ltc: { networks: { litecoin: CHAIN_NETWORKS[CHAIN_CONSTANTS.LITECOIN] } },
  bnb: { networks: { bep20: CHAIN_NETWORKS[CHAIN_CONSTANTS.BEP20] } },
  sol: { networks: { solana: CHAIN_NETWORKS[CHAIN_CONSTANTS.SOLANA] } },
  ton: { networks: { ton: CHAIN_NETWORKS[CHAIN_CONSTANTS.TON] } },
  pyusd: { networks: { erc20: CHAIN_NETWORKS[CHAIN_CONSTANTS.ERC20] } },
} as const;

type CoinConfig = typeof COIN_CONFIG;
type ChainKey = {
  [K in keyof CoinConfig]: keyof CoinConfig[K]["networks"];
}[keyof CoinConfig];
type CoinsForChain<C extends ChainKey> = {
  [K in keyof CoinConfig]: C extends keyof CoinConfig[K]["networks"]
    ? K
    : never;
}[keyof CoinConfig];

/**
 * return the config for the chain
 * @example
 * CHAIN_CONFIG.erc20.coins.eth
 * CHAIN_CONFIG.erc20.coins.btc
 * CHAIN_CONFIG.erc20.coins.trx
 * CHAIN_CONFIG.erc20.coins.usdt
 * CHAIN_CONFIG.erc20.coins.usdc
 * CHAIN_CONFIG.erc20.coins.ltc
 * CHAIN_CONFIG.erc20.coins.bnb
 */
export const CHAIN_CONFIG = buildChainConfig(COIN_CONFIG);

function buildChainConfig(coins: CoinConfig): {
  [C in ChainKey]: { coins: { [K in CoinsForChain<C>]: COIN_CONSTANTS } };
} {
  const out: Record<string, { coins: Record<string, COIN_CONSTANTS> }> = {};
  for (const coin of Object.keys(coins) as (keyof CoinConfig)[]) {
    const nets = coins[coin].networks;
    for (const chain of Object.keys(nets) as ChainKey[]) {
      if (!out[chain]) out[chain] = { coins: {} };
      out[chain].coins[coin] = coin as COIN_CONSTANTS;
    }
  }
  return out as {
    [C in ChainKey]: { coins: { [K in CoinsForChain<C>]: COIN_CONSTANTS } };
  };
}

export function hasMultipleNetworks(symbol: string): boolean {
  const config = COIN_CONFIG[symbol as keyof typeof COIN_CONFIG];
  return config ? Object.keys(config.networks).length > 1 : false;
}

export function getSingleNetworkName(symbol: string): string {
  const config = COIN_CONFIG[symbol as keyof typeof COIN_CONFIG];
  if (!config) return "";
  const list = Object.values(config.networks);
  return list.length === 1 ? list[0].name : "";
}
